import React, {FC, useRef} from "react";
import {Alert, message, Modal, Table} from "antd";
import {PureQueryOptions, useApolloClient} from "@apollo/client";
import {
    LessonBookingIntentInput,
    LessonBookingPlanApplies,
    LessonBookingPlanSummaryDocument,
    LessonBookingPlanSummaryQuery,
    LessonBookingPlanSummaryQueryVariables,
    UpdateCourseAndBookingsInput,
    useApplyLessonBookingPlanMutation,
} from "../../../generated/graphql";
import {renderAmount} from "../../../helpers/renderAmount";

// Speichern im Modus „Neu“ (ADR 0020 Stufe 2, nur Superuser): der Server plant alle Buchungen, der Superuser
// bestätigt die Zusammenfassung, dann speichert der Server mit den Fingerabdrücken. Hat sich eine Buchung seither
// geändert (z. B. Zahlung eingegangen), lehnt er mit „Vorschau veraltet“ ab und nichts wird geschrieben.
// Stufe 3: Je nach Tenant-Einstellung entstehen danach im Hintergrund Korrekturrechnungen; die Zusammenfassung nennt sie.
// Stufe 4: Die Entscheidungen des Büros je Buchung (intents) gehen mit Zusammenfassung und Speichern an den Server.

type Plan = LessonBookingPlanSummaryQuery["lessonBookingPlanSummary"][number];

const eur = (amount: number) => renderAmount({amount});

const correctionText = (corrections: number, mails: number) => {
    if (corrections === 0) return "Es entsteht keine Korrekturrechnung.";
    const invoices = corrections === 1 ? "eine Korrekturrechnung" : `${corrections} Korrekturrechnungen`;
    let mailPart = "";

    if (mails > 0) mailPart = mails === corrections ? `, ${corrections === 1 ? "sie geht" : "alle gehen"} per E-Mail an die Bucher` : `, ${mails} davon per E-Mail an die Bucher`;

    return `Nach dem Speichern ${corrections === 1 ? "entsteht" : "entstehen"} im Hintergrund ${invoices}${mailPart}. Das dauert einige Sekunden je Buchung.`;
};

const changes = (plan: Plan) => plan.unitsAdded > 0 || plan.unitsRemoved > 0 || plan.ratesChanged > 0 || plan.credit > 0 || plan.staffNoteChanged;

export const NewModelSaveSummary: FC<{plans: Array<Plan>}> = ({plans}) => {
    const count = (applies: LessonBookingPlanApplies) => plans.filter((plan) => plan.applies === applies).length;
    const changed = plans.filter((plan) => plan.applies !== LessonBookingPlanApplies.None && changes(plan));
    const corrections = plans.filter((plan) => plan.correctionInvoice).length;
    const mails = plans.filter((plan) => plan.correctionInvoiceMail).length;

    return (
        <div>
            <p>
                {`${plans.length} Buchungen: ${count(LessonBookingPlanApplies.Money)} nach Einheiten × Einheitenpreis, ${count(
                    LessonBookingPlanApplies.Units,
                )} nur Einheiten (Preis und Raten bleiben), ${count(LessonBookingPlanApplies.None)} unverändert (storniert oder eingeschränkt).`}
            </p>
            {changed.length === 0 ? (
                <p>{"Keine Buchung ändert sich."}</p>
            ) : (
                <Table<Plan>
                    size="small"
                    rowKey="bookingId"
                    pagination={false}
                    scroll={{y: 320}}
                    dataSource={changed}
                    columns={[
                        {title: "Buchung", dataIndex: "bookingLabel", width: 90},
                        {title: "Einheiten", key: "units", width: 90, render: (_, plan) => `+${plan.unitsAdded} / −${plan.unitsRemoved}`},
                        {
                            title: "Preis",
                            key: "price",
                            width: 170,
                            render: (_, plan) =>
                                plan.applies === LessonBookingPlanApplies.Money && plan.priceAfter !== plan.priceBefore ? `${eur(plan.priceBefore)} → ${eur(plan.priceAfter)}` : "unverändert",
                        },
                        {title: "Guthaben", key: "credit", width: 90, render: (_, plan) => (plan.credit > 0 ? eur(plan.credit) : "")},
                        {title: "Notiz", key: "staffNote", width: 80, render: (_, plan) => (plan.staffNoteChanged ? "geändert" : "")},
                        {
                            title: "Korrekturrechnung",
                            key: "correctionInvoice",
                            width: 140,
                            render: (_, plan) => {
                                if (plan.correctionInvoiceMail) return "ja, per E-Mail";

                                return plan.correctionInvoice ? "ja" : "";
                            },
                        },
                        {
                            title: "Hinweise",
                            key: "warnings",
                            render: (_, plan) => (
                                <div style={{fontSize: "0.85em"}}>
                                    {plan.warnings.map((warning) => (
                                        <div key={warning}>{warning}</div>
                                    ))}
                                </div>
                            ),
                        },
                    ]}
                />
            )}
            <Alert
                style={{marginTop: 12}}
                type="info"
                showIcon
                message={`${correctionText(corrections, mails)} Bezahlte und exportierte Raten bleiben unverändert.`}
            />
        </div>
    );
};

const errorMessage = (error: unknown) => (error as {graphQLErrors?: Array<{message: string}>})?.graphQLErrors?.[0]?.message ?? (error as Error)?.message ?? "Unbekannter Fehler";

// Liefert true, wenn gespeichert wurde; false bei Abbruch oder Fehler (Fehler zeigt der Hook selbst an).
// refetchQueries wie beim alten Speichern, sonst zeigen offene Kursdetails danach alte Termine und Beträge.
export const useNewModelSave = (refetchQueries?: Array<string | PureQueryOptions>) => {
    const client = useApolloClient();
    const [applyPlan] = useApplyLessonBookingPlanMutation({refetchQueries});
    // Ein zweiter Klick auf Speichern während Planung oder Bestätigung startet keinen zweiten Lauf.
    const busy = useRef(false);

    const save = async (input: UpdateCourseAndBookingsInput, intents: Array<LessonBookingIntentInput>): Promise<boolean> => {
        const hideLoading = message.loading("Buchungen werden geplant …", 0);
        let plans: Array<Plan>;

        try {
            const {data} = await client.query<LessonBookingPlanSummaryQuery, LessonBookingPlanSummaryQueryVariables>({
                query: LessonBookingPlanSummaryDocument,
                variables: {updateCourseAndBookingsInput: input, intents},
                fetchPolicy: "no-cache",
            });

            plans = data.lessonBookingPlanSummary;
        } catch (error) {
            Modal.error({title: "Kurs nicht gespeichert", content: errorMessage(error), okText: "Verstanden", width: 560});

            return false;
        } finally {
            hideLoading();
        }

        const blocked = plans.filter((plan) => plan.blocker);

        if (blocked.length > 0) {
            Modal.error({
                title: "Nicht im neuen Preismodell möglich",
                width: 640,
                content: (
                    <div>
                        <p>{"Diese Buchungen bitte zuerst einzeln anpassen oder im Modus „Heute“ speichern:"}</p>
                        <ul>
                            {blocked.map((plan) => (
                                <li key={plan.bookingId}>{`${plan.bookingLabel}: ${plan.blocker}`}</li>
                            ))}
                        </ul>
                    </div>
                ),
                okText: "Verstanden",
            });

            return false;
        }
        if (plans.some((plan) => plan.regenerated && plan.applies !== LessonBookingPlanApplies.None)) {
            Modal.error({title: "Nicht im neuen Preismodell möglich", content: "Alle Kurseinheiten wurden neu erzeugt. Bitte im Modus „Heute“ speichern.", okText: "Verstanden"});

            return false;
        }

        const outcome = await new Promise<{saved: boolean; error?: unknown}>((resolve) =>
            Modal.confirm({
                title: "Im neuen Preismodell speichern?",
                width: 900,
                content: <NewModelSaveSummary plans={plans} />,
                okText: "Speichern",
                cancelText: "Abbrechen",
                // Promise zurückgeben: antd zeigt am Knopf die Ladeanzeige, bis der Server geantwortet hat.
                onOk: () =>
                    applyPlan({variables: {updateCourseAndBookingsInput: input, intents, fingerprints: plans.map((plan) => ({bookingId: plan.bookingId, fingerprint: plan.fingerprint}))}}).then(
                        () => resolve({saved: true}),
                        (error) => resolve({saved: false, error}),
                    ),
                onCancel: () => resolve({saved: false}),
            }),
        );

        if (outcome.error) {
            Modal.error({title: "Kurs nicht gespeichert", content: errorMessage(outcome.error), okText: "Verstanden", width: 560});
        }

        return outcome.saved;
    };

    return async (input: UpdateCourseAndBookingsInput, intents: Array<LessonBookingIntentInput> = []): Promise<boolean> => {
        if (busy.current) return false;
        busy.current = true;
        try {
            return await save(input, intents);
        } finally {
            busy.current = false;
        }
    };
};
